import { z } from "zod/v4"
import type { DocumentSnapshotLike } from "../firestore"
import { StampLaneId } from "../id"
import { CreatedAt, UpdatedAt } from "./system"
import type { UserStampCardStateDocument } from "./user-stamp-card-state"
import { type UserSubscription, UserSubscriptions } from "./user-private"

export const UnlockableBy = z.object({
  coin: z.boolean().describe("コインで解放可能かどうか"),
  premium: z.boolean().describe("プレミアム会員で解放可能かどうか"),
}).describe("レーン解放条件")

export type UnlockableBy = z.infer<typeof UnlockableBy>

export const StampLaneDocumentMeta = z.object({
  unlockable_by: UnlockableBy,
  unlock_coin_price: z.int().min(0).optional().describe("レーン解放に必要なコイン数（unlockable_by.coin=trueの場合のみ）"),
}).check((ctx) => {
  const data = ctx.value;
  if (data.unlockable_by.coin) {
    if (data.unlock_coin_price == null) {
      ctx.issues.push({
        code: "custom",
        path: ["unlock_coin_price"],
        message: "coin=trueの場合はunlock_coin_priceが必須です",
        input: data.unlock_coin_price,
      })
    }
  } else if (data.unlock_coin_price != null) {
      ctx.issues.push({
        code: "custom",
        path: ["unlock_coin_price"],
        message: "coin=falseの場合はunlock_coin_priceを指定できません",
        input: data.unlock_coin_price,
      })
    }
})

export type StampLaneDocumentMeta = z.infer<typeof StampLaneDocumentMeta>

export const StampLaneDocumentBody = z.intersection(
  StampLaneDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type StampLaneDocumentBody = z.infer<typeof StampLaneDocumentBody>

export type StampLaneDocument = StampLaneDocumentBody & {
  id: StampLaneId,
}

/**
 * レーンがスーパーレーンかどうかを判定する
 * @param meta レーンのメタデータ
 * @returns スーパーレーンの場合true、ノーマルレーンの場合false
 */
export function isSuperLane(meta: { unlockable_by: UnlockableBy }): boolean {
  return meta.unlockable_by.coin || meta.unlockable_by.premium
}

export const CreateStampLaneRequest = z.object({
  unlockableBy: UnlockableBy,
  unlockCoinPrice: z.int().min(0).optional(),
}).check((ctx) => {
  const data = ctx.value;
  if (data.unlockableBy.coin) {
    if (data.unlockCoinPrice == null) {
      ctx.issues.push({
        code: "custom",
        path: ["unlockCoinPrice"],
        message: "coin=trueの場合はunlockCoinPriceが必須です",
        input: data.unlockCoinPrice,
      })
    }
  } else if (data.unlockCoinPrice != null) {
      ctx.issues.push({
        code: "custom",
        path: ["unlockCoinPrice"],
        message: "coin=falseの場合はunlockCoinPriceを指定できません",
        input: data.unlockCoinPrice,
      })
    }
})

export type CreateStampLaneRequest = z.infer<typeof CreateStampLaneRequest>

export const UpdateStampLaneRequest = z.object({
  unlockableBy: UnlockableBy.optional(),
  unlockCoinPrice: z.int().min(0).optional(),
})

export type UpdateStampLaneRequest = z.infer<typeof UpdateStampLaneRequest>

export class StampLaneDocuments {

  static toDocumentData(doc: StampLaneDocumentBody): object {
    return {
      ...doc,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): StampLaneDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: StampLaneId(doc.id),
      unlockable_by: data["unlockable_by"],
      unlock_coin_price: data["unlock_coin_price"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }

  /**
   * レーンの報酬がユーザーに対して受取可能かどうかを判定する
   *
   * - ノーマルレーン: 常に受取可能
   * - スーパーレーン: 以下のいずれかの条件を満たせば受取可能（OR条件）
   *   - プレミアム解放: unlockable_by.premium = true かつユーザーがミッション期間開始時点でプレミアム会員だった場合
   *   - コイン解放: unlockable_by.coin = true かつユーザーがコインで解放済みの場合
   *
   * ※プレミアム会員の状態で途中でプレミアム会員を解約しても、プレミアム会員の有効期間内で
   * スタンプカードのミッション期間開始時点でプレミアムであればスーパーレーンは利用可能
   * ※報酬はミッション期間開始後から受け取り可能なため、mission_period.start_atを使用
   */
  static canClaimRewards(
    lane: StampLaneDocument,
    stampCardState: UserStampCardStateDocument | null,
    userSubscription: UserSubscription | null,
    missionPeriodStartAt: number
  ): boolean {
    if (!isSuperLane(lane)) {
      return true
    }

    if (lane.unlockable_by.premium && UserSubscriptions.isPremiumUser(userSubscription, missionPeriodStartAt)) {
      return true
    }

    return !!(lane.unlockable_by.coin && stampCardState && stampCardState.super_lane_unlock_by_coin)
  }
}
